import { useQuery } from "react-query";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { Sort } from "@/components/atoms";
import usePaginationFromEndpoint, {
    usePaginationFromEndpointProps,
} from "@/components/templates/PaginatedList/usePaginationFromEndpoint";
import useSearchFromEndpoint, {
    useSearchFromEndpointProps,
} from "@/components/templates/SearchList/useSearchFromEndpoint";
import { FieldPath } from "@/core/fieldPath";
import { contractApi } from "@/shared/apis";
import { ContractCategoryQueryParameters, searchableContractMapper } from "@/shared/apis/contractApi";
import { Contract, ContractPagination, ContractSortProperty, SearchableContract, SortOrder } from "@/shared/domain";
import { useGlobalFilter } from "@/shared/hooks";
import { usePolicyholders } from "@/shared/hooks/usePolicyholders";

type useContractQueriesProps = {
    tableMode: boolean;
    policyholders: number[];
    useContractsPageQuery: ({ defaultCategory }) => usePaginationFromEndpointProps<Contract, ContractSortProperty>;
    useContractsCategoriesQuery: ({ enabled }) => useContractCategoriesQueryProps;
    useContractsSearchQuery: (filterProperties?: { textSearch; sortProperty }) => useSearchFromEndpointProps<Contract>;
};

type useContractCategoriesQueryProps = {
    error: unknown;
    contractCategories: MyBrokerApi.CategoryCounters[];
    isLoadingCategories: boolean;
    isFetchedCategories: boolean;
};

const defaultSorting: Sort<ContractSortProperty> = {
    property: "effectiveDate",
    order: SortOrder.Descending,
};

export const contractSearchFilters: FieldPath<SearchableContract>[] = [
    "branch.name",
    "state.name",
    "referenceString",
    "policyHolder",
    "dueDateString",
    "periodicity.name",
    "amountString",
    "risks.type.name",
    "risks.name",
    "warrantiesList.warranty.name",
];

const useContractQueries = (policyholder?: number): useContractQueriesProps => {
    const { policyholders } = usePolicyholders();
    const { isProEnvironment } = useGlobalFilter();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const tableMode = isProEnvironment && !isSmallScreen;
    const policyholdersIdList = policyholder ? [policyholder] : policyholders;

    const useContractsPageQuery = ({ defaultCategory }) => {
        return usePaginationFromEndpoint<
            Contract,
            ContractPagination,
            ContractCategoryQueryParameters,
            ContractSortProperty
        >({
            objectType: "contract",
            tableMode,
            policyholdersIdList,
            endPoint: contractApi.getContracts,
            defaultCategory,
            defaultSorting,
        });
    };

    const useContractsCategoriesQuery = ({ enabled = true }: { enabled: boolean }): useContractCategoriesQueryProps => {
        const {
            data: contractCategories = [],
            isLoading: isLoadingCategories,
            isFetched: isFetchedCategories,
            error,
        } = useQuery(
            ["contractCategories", { policyholders }],
            () => contractApi.getContractCategories(policyholders),
            { enabled },
        );
        return {
            error,
            contractCategories,
            isLoadingCategories,
            isFetchedCategories,
        };
    };

    const useContractsSearchQuery = ({ textSearch, sortProperty } = { textSearch: "", sortProperty: "" }) => {
        const filterContracts = (results: Contract[]) => {
            let contracts = searchableContractMapper(results);
            if (textSearch) {
                contracts = contracts.filterBy(textSearch, contractSearchFilters);
            }
            if (sortProperty) {
                contracts = contracts.sortBy(sortProperty);
            }
            return contracts;
        };

        return useSearchFromEndpoint<Contract, ContractPagination, ContractCategoryQueryParameters>(
            "contracts",
            contractApi.getContractsLazy,
            policyholdersIdList,
            filterContracts,
        );
    };

    return {
        tableMode,
        policyholders: policyholdersIdList,
        useContractsPageQuery,
        useContractsCategoriesQuery,
        useContractsSearchQuery,
    };
};

export default useContractQueries;
